export const VISION = {
  title: "Turn free time into time well spent.",
  subtitle: "Outbound is a personalized, map-first planner that answers one everyday question: what could I do with my free time? Its job is to get people out of the house and into the world, not to keep them in an app.",
  founder: "Joaquin German Olea, United States",
  sections: [
    {
      id: "vision", title: "Product vision & pitch",
      blocks: [
        { quote: "You know you want to see a movie on Friday. Outbound turns it into an evening: a quiet café before, a free gallery on the way, and a dinner spot you'll love after." },
        { p: "People often know one thing they want to do but struggle to build an enjoyable experience around it. Outbound connects activities into a flexible itinerary shaped by each person's likes, dislikes, budget, location, transportation and available time. Every stop explains why it was chosen for them, and nothing has to be followed exactly." },
        { h: "Concise pitch" },
        { p: "Outbound is the GPS for free time. Tell it how long you have, or one thing you already want to do, and it maps a personalized outing of real nearby places, from free parks and libraries to cafés and restaurants. Plan it from home, share it with friends, and spend less effort organizing and more time enjoying." },
        { h: "What success means" },
        { p: "Success is measured by outings completed and new places visited, not by minutes spent in the app. A great session is a short one followed by a good afternoon outside." },
      ],
    },
    {
      id: "audiences", title: "Target audiences & problems solved",
      blocks: [
        { ul: [
          "Locals with an open evening or weekend who want something better than \"the usual\" and are tired of defaulting to staying in.",
          "Travelers visiting a US city who want to explore beyond the top tourist list, on their own budget and pace.",
          "Couples who want a date that reflects both people, not just one dinner reservation.",
          "Families who need kid-friendly, affordable, accessible stops that fit nap times and attention spans.",
          "Students and budget-conscious people who want meaningful outings built on free and low-cost places such as parks, libraries, museums and public art.",
          "Groups of friends with different ideas of a perfect day who need a plan everyone can see and join.",
        ] },
        { h: "Problems we solve" },
        { ul: [
          "Planning friction: getting from \"I'm free\" to \"let's go\" today means juggling maps, event listings, review sites and group chats.",
          "Decision fatigue: too many options, and few that reflect the person.",
          "The anchor problem: people know the main event but not what to do before or after it.",
          "Cost anxiety: many assume a good outing needs to be expensive.",
          "Group coordination: who's coming to which part, without endless messages.",
        ] },
        { h: "Why people come back (versus separate tools)" },
        { ul: [
          "One place connects the whole experience: discovery, timing, travel, cost and sharing. Separate apps handle only one piece each.",
          "It gets more personal over time. Feedback, check-ins and \"not for me\" reasons shape the next plan.",
          "\"I'm free now\" makes spontaneous time usable in seconds.",
          "A private outings journal and personal discovery map show how much of your city you've actually experienced.",
          "Shared plans with per-stop \"I'm in\" replace long group-chat threads.",
        ] },
      ],
    },
    {
      id: "journey", title: "Map-first user journey & screens",
      blocks: [
        { p: "The interface is familiar like a GPS app: a full-screen map with numbered stops and a route line, alongside an itinerary panel (side panel on desktop, below the map on mobile)." },
        { h: "Core journey" },
        { ul: [
          "1. Sign up and build a taste profile in about two minutes: interests, dislikes, vibe, diet, accessibility, activity level, pace, default budget, transport and a private home area.",
          "2. Choose \"Plan something for me\", \"Build around an activity\" (e.g. a 7pm movie), or tap \"I'm free now\".",
          "3. Set the time window, budget, starting point, travel distance, transport and group type.",
          "4. See suggested stops on the map, each with estimated cost, listed hours (or \"hours not confirmed\"), travel time from the previous stop, booking hints and a short \"why this fits you\".",
          "5. Skip, replace, reorder (drag or arrows) or add stops. Times and totals recalculate.",
          "6. Save privately, share with chosen friends, and let friends mark \"I'm in\" for specific stops.",
          "7. Go out. Afterwards, a quick \"How was it?\" check-in adds the outing to the journal and improves future plans.",
        ] },
        { h: "Screens" },
        { ul: [
          "Sign in / create account",
          "Taste profile onboarding (also editable later)",
          "Planner: map + input form",
          "Itinerary: map + editable stop cards, totals, warnings, save/share/check-in",
          "I'm free now: one-tap quick outing",
          "My Plans: saved plans and plans shared with me",
          "My Outings: private journal, stats and personal discovery map",
          "Friends & Privacy: friend requests, access requests from invite links, and a per-plan list of who can see what",
          "Invite: request access to a plan (approval required)",
          "Vision: this document",
        ] },
      ],
    },
    {
      id: "mvp", title: "Focused MVP vs later features",
      blocks: [
        { h: "Phase 1 (this prototype)" },
        { ul: [
          "Email/password accounts and guided taste-profile onboarding.",
          "Interactive map planner for any US city using real OpenStreetMap places.",
          "AI itinerary building, personalized to the profile, with a short reason per stop; a simple rules-based fallback if AI is unavailable.",
          "Both entry points, all planning inputs, and edit actions: skip, replace with alternatives, reorder, add.",
          "One-tap \"I'm free now\".",
          "Free and low-cost options always included when available.",
          "Private-by-default saved plans; friend requests that need acceptance; per-plan sharing with revocation; invite links that only request access; start point hidden unless the member shows it.",
          "Per-stop \"I'm in\" for approved friends.",
          "Feedback on stops and outings; report-a-problem on stops.",
          "\"My outings\" journal with a discovery map and post-outing check-in.",
          "Optional in-app \"Free this weekend?\" suggestion (no email or push).",
        ] },
        { h: "Later" },
        { ul: [
          "Live events, movie showtimes, ticketing and reservations.",
          "Live transit routing and exact travel times.",
          "Time-limited live location sharing: off by default, only for approved friends, set per outing, and ends automatically.",
          "Business member deals and clearly labeled sponsored suggestions (shown in Phase 1 only as a labeled SAMPLE).",
          "Native iOS and Android apps, offline maps, and calendar integration.",
          "Group preference blending (a plan that balances several profiles).",
        ] },
      ],
    },
    {
      id: "business", title: "Business model hypotheses",
      blocks: [
        { p: "These are hypotheses to test, not conclusions. No revenue, demand or valuation figures are assumed." },
        { ul: [
          "Premium subscription: possible features include group preference blending, advanced filters, multi-day trip planning, offline maps and deeper journal insights. Hypothesis: frequent planners will pay for convenience, while the core free experience stays genuinely useful.",
          "Booking referrals: commissions when members book tickets, tables or experiences through partner integrations. Hypothesis: a well-timed booking link inside a plan converts better than a generic listing.",
          "Business partnerships: tools for local venues to keep listings accurate, publish events and understand anonymized, aggregated interest. Hypothesis: small businesses value qualified local discovery.",
          "Member offers: deals from restaurants, cinemas, entertainment venues, fairs and events. Hypothesis: offers raise outing completion without hurting trust, if they stay relevant.",
          "Sponsored suggestions: always clearly labeled, only shown when they fit the plan's constraints, and never replacing the free options. Hypothesis: relevance rules protect trust while creating revenue.",
        ] },
        { h: "Guardrails" },
        { ul: [
          "Ranking for organic suggestions is never sold.",
          "Sponsored content is labeled at every point it appears.",
          "No sale of personal or location data.",
        ] },
      ],
    },
    {
      id: "validation", title: "Market-validation plan",
      blocks: [
        { h: "Research" },
        { ul: [
          "20–30 interviews across locals, travelers, couples, families, students and friend groups about how they plan free time today and what goes wrong.",
          "Diary study: participants log free-time decisions for two weeks.",
          "Concierge test: plan outings by hand for a small group, then compare with the app's plans.",
          "Interviews with 10–15 local businesses about discovery, listings and offers.",
        ] },
        { h: "Behaviors that would show commercial potential" },
        { ul: [
          "Plans generated that turn into completed outings (check-ins).",
          "Repeat outings per member over 4–8 weeks without reminders driving it.",
          "Share rate, and how often invited friends create their own accounts and plans.",
          "Edits per plan trending down over time (personalization is working).",
          "Clicks on booking links and the share of members willing to pay for a premium feature in a pricing test.",
          "Business willingness to claim listings or pilot an offer.",
        ] },
        { h: "Pilot" },
        { p: "Launch in one or two US cities with dense, free-to-use public spaces. Recruit through campuses, libraries, local newsletters and community organizations. Run 8–12 weeks and decide go/no-go per hypothesis based on the behaviors above." },
      ],
    },
    {
      id: "society", title: "Societal benefits & how to measure them",
      blocks: [
        { ul: [
          "Real-world connection: plans are built to be shared and joined in person.",
          "Getting out of the house: the app is designed to be used briefly and then put away.",
          "Curiosity and discovery: suggestions mix the familiar with places people haven't tried.",
          "Cultural participation: museums, galleries, public art, libraries and community spaces are first-class stops.",
          "Local business discovery: independent cafés, bookstores and restaurants appear alongside free public spaces.",
          "Affordability: a meaningful outing never requires an expensive purchase, and a free budget is a real option.",
        ] },
        { h: "Measures" },
        { ul: [
          "Outings completed per member per month.",
          "New places tried, and new neighborhoods visited.",
          "Time spent out (from check-ins) compared with time spent in the app.",
          "Share of stops that are free or low-cost, and share that are independent businesses or community spaces.",
          "Outings shared with friends and stops joined (\"I'm in\").",
          "Self-reported wellbeing and connection in optional, occasional surveys.",
        ] },
      ],
    },
    {
      id: "technology", title: "Technology roadmap & data integrations",
      blocks: [
        { h: "Now" },
        { ul: [
          "Places: OpenStreetMap via Overpass (free, nationwide). Addresses via Nominatim geocoding.",
          "Maps: Leaflet with OpenStreetMap tiles (a commercial tile provider would be needed at scale).",
          "AI planning: a large language model chooses and orders stops from real candidate places and writes a short reason for each; it is never allowed to invent places.",
          "Travel estimates: distance-based estimates per transport mode, clearly labeled.",
        ] },
        { h: "Next" },
        { ul: [
          "Events and showtimes: ticketing and event APIs, cinema showtime feeds, library and city event calendars.",
          "Reservations: restaurant reservation partners.",
          "Transit: GTFS and real-time transit feeds for accurate routing and timing.",
          "Live hours and status: business-verified listings and place-data providers, plus member reports.",
          "Weather: adapt outdoor stops to forecasts.",
        ] },
        { h: "Later" },
        { ul: [
          "Learned recommendation models that combine profile, feedback and context (time, weather, group).",
          "Group preference blending for plans that balance several people.",
          "Re-planning on the go when a stop is closed or running late.",
          "Time-limited, opt-in live location sharing for meetups.",
        ] },
        { p: "The goal of every advance: less effort organizing, more time enjoying." },
      ],
    },
    {
      id: "trust", title: "Privacy, trust & data quality",
      blocks: [
        { h: "Privacy and user control" },
        { ul: [
          "Every plan is private by default. No public links.",
          "Friends must have an account and accept a request. Friendship gives no automatic access.",
          "Access is granted per plan, per friend, and can be revoked at any time; revoking hides the plan right away.",
          "Invite links only let someone ask for access. The member approves or declines.",
          "Starting point and home area are hidden unless the member turns on \"show my starting point\" for that plan.",
          "Location is never collected in the background. The starting point can be typed instead of detected.",
          "A Sharing & privacy screen lists everyone who can see each plan, with one-tap removal.",
        ] },
        { h: "Handling uncertain information" },
        { ul: [
          "Outdated hours: hours are shown only when listed and labeled as listed; otherwise \"hours not confirmed, check before going\".",
          "Uncertain prices: always shown as estimated ranges.",
          "Unavailable events or closed places: report-a-problem flags the stop, stops suggesting it to that member, and offers alternatives.",
          "Recommendations that don't fit: skip, replace or \"not for me\" with a reason, which feeds future plans.",
          "Time and budget conflicts are surfaced as clear warnings with suggested fixes.",
        ] },
      ],
    },
    {
      id: "risks", title: "Main risks & assumptions",
      blocks: [
        { h: "Assumptions to test" },
        { ul: [
          "People want help planning around free time often enough to return.",
          "Open map data is rich enough in target cities for good plans.",
          "Personalized reasons increase trust and completion.",
          "Friends will use per-stop \"I'm in\" instead of group chats.",
        ] },
        { h: "Risks" },
        { ul: [
          "Data quality: open data can have gaps or outdated hours. Mitigation: honest labels, member reports, verified listings later.",
          "Cold start: new members have little feedback. Mitigation: a quick taste profile and smart defaults.",
          "Competition from large map and review platforms. Mitigation: focus on the whole experience, personalization and getting people out, not on listings.",
          "Trust erosion from sponsored content. Mitigation: strict labeling and relevance rules.",
          "Privacy incidents. Mitigation: private by default, minimal data, no background location.",
          "AI errors. Mitigation: AI may only choose from real candidate places; rules-based fallback; easy edits.",
          "Engagement traps. Mitigation: success metrics based on time outside, not time in the app.",
        ] },
      ],
    },
    {
      id: "next", title: "Practical next steps",
      blocks: [
        { ul: [
          "Weeks 1–2: run interviews and the concierge test; refine the taste profile questions.",
          "Weeks 2–4: put this prototype in front of 30–50 pilot members in one city; instrument completion, edits, shares and check-ins.",
          "Weeks 4–8: add one events integration and one transit source in the pilot city; test a premium feature with a pricing page.",
          "Weeks 6–10: recruit 5–10 local businesses for listing accuracy and a clearly labeled offer pilot.",
          "Week 12: review the behaviors in the validation plan and decide what to build next.",
        ] },
        { quote: "Help people turn available time into personalized experiences, discover more of the world around them, and connect with others." },
      ],
    },
  ],
};

export const visionMarkdown = () => {
  const out = [`# ${VISION.title}`, "", VISION.subtitle, "", `Founder: ${VISION.founder}`, ""];
  VISION.sections.forEach((s, i) => {
    out.push(`## ${i + 1}. ${s.title}`, "");
    s.blocks.forEach((b) => {
      if (b.h) out.push(`### ${b.h}`, "");
      else if (b.ul) out.push(...b.ul.map((x) => `- ${x}`), "");
      else if (b.quote) out.push(`> ${b.quote}`, "");
      else out.push(b.p, "");
    });
  });
  return out.join("\n");
};
